/**
 * The @ suggester's list (#1599 Story 1.4, AC 4; frames F1-F5 of the
 * mention-indicator wireframes): presentational, RN primitives only, so Story
 * 3.1 can mount the same component above the native keyboard. The web glue
 * that positions it and drives the keys is `mentionSuggesterHost.web.tsx`.
 *
 * ⚠️ NO ROLE LABELS AND NO SECOND SOURCE. Every row is a candidate the server
 * returned (AD-1): initials, name, email. The team row ("Vertiv Config Team",
 * subtitle "Every Configuration Engineer") is drawn only when the reply
 * carried `group`, and is picked like a person row (Story 1.7).
 *
 * ⚠️ ROLES. React Native Web's role map has no `listbox`/`option`, so the
 * container is a `list` and each row a `button` whose selected state is
 * announced through `accessibilityState={{ selected }}` (RNW renders it as
 * `aria-selected`). The row count is announced in the list's label.
 *
 * Pins are style-contract and keyboard only; width, row height and anchoring
 * are the walk's (`correspondence.md`, "a ceiling is not a width").
 */
import { Users } from 'lucide-react-native';
import React, { useEffect, useRef } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { initials } from './format';
import { colors } from '../../theme/colors';
import { fontSize } from '../../theme/typography';

import type { MentionCandidate } from './mentionCandidates';

export const MENTION_SUGGESTER_FAILED = "Couldn't load names";
export const MENTION_SUGGESTER_EMPTY = 'No one to mention here';

/** 28pt disc, the thread row's avatar size (wireframes, `.sug .av`). */
const DISC = 28;
const ROW_HEIGHT = 44;
/** About five and a half rows, so a sixth says "there is more". */
const MAX_LIST_HEIGHT = Math.round(ROW_HEIGHT * 5.5);
export const MENTION_SUGGESTER_WIDTH = 280;

export interface MentionSuggesterProps {
  items: readonly MentionCandidate[];
  selectedIndex: number;
  status: 'ready' | 'loading' | 'failed';
  onPick(item: MentionCandidate): void;
  onHover(index: number): void;
  /**
   * A narrower box than `MENTION_SUGGESTER_WIDTH` (#1599 Story 3.1): the
   * native composer passes its field's width at 320pt, where 280 would run
   * past the card's edge and the overhang would take no taps (Story 8.6).
   * Never wider than the default.
   */
  maxWidth?: number;
  testID?: string;
}

const rowKey = (item: MentionCandidate): string =>
  item.kind === 'person' ? `person-${item.userId}` : `group-${item.id}`;

const Row: React.FC<{
  item: MentionCandidate;
  index: number;
  selected: boolean;
  onPick(item: MentionCandidate): void;
  onHover(index: number): void;
  testID: string;
}> = ({ item, index, selected, onPick, onHover, testID }) => {
  const ref = useRef<View>(null);
  // Read inside the component: a module-scope read freezes the web branch
  // off under test (`correspondence.md`, Story 7.2).
  const isWeb = Platform.OS === 'web';
  useEffect(() => {
    if (!selected || !isWeb) return;
    const node = ref.current as unknown as {
      scrollIntoView?: (options: { block: string }) => void;
    } | null;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [selected, isWeb]);
  const secondary = item.kind === 'person' ? item.email : item.subtitle;
  return (
    <Pressable
      ref={ref}
      onPress={() => onPick(item)}
      onHoverIn={() => onHover(index)}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={`${item.name}, ${secondary}`}
      style={[styles.row, selected ? styles.rowSelected : null]}
      testID={testID}
    >
      <View style={styles.disc}>
        {item.kind === 'person' ? (
          <Text style={styles.discText}>{initials(item.name)}</Text>
        ) : (
          <Users size={16} color={colors.textPrimary} />
        )}
      </View>
      <View style={styles.text}>
        <Text style={styles.name} numberOfLines={1}>
          {item.name}
        </Text>
        <Text style={styles.secondary} numberOfLines={1}>
          {secondary}
        </Text>
      </View>
    </Pressable>
  );
};

export const MentionSuggester: React.FC<MentionSuggesterProps> = ({
  items,
  selectedIndex,
  status,
  onPick,
  onHover,
  maxWidth,
  testID = 'correspondence-mention-suggester',
}) => {
  const box =
    typeof maxWidth === 'number' &&
    maxWidth > 0 &&
    maxWidth < MENTION_SUGGESTER_WIDTH
      ? [styles.box, { width: maxWidth }]
      : styles.box;
  if (items.length === 0) {
    // Loading with nothing to show yet: no box at all, rather than a flash
    // of "No one to mention here" before the list lands.
    if (status === 'loading') return null;
    return (
      <View
        style={box}
        testID={testID}
        accessibilityRole="list"
        accessibilityLabel={
          status === 'failed'
            ? MENTION_SUGGESTER_FAILED
            : MENTION_SUGGESTER_EMPTY
        }
      >
        <Text style={styles.empty} testID={`${testID}-empty`}>
          {status === 'failed'
            ? MENTION_SUGGESTER_FAILED
            : MENTION_SUGGESTER_EMPTY}
        </Text>
      </View>
    );
  }
  return (
    <View
      style={box}
      testID={testID}
      accessibilityRole="list"
      accessibilityLabel={`${items.length} ${
        items.length === 1 ? 'person' : 'people'
      } to mention`}
    >
      <ScrollView style={styles.scroll} keyboardShouldPersistTaps="always">
        {items.map((item, index) => (
          <Row
            key={rowKey(item)}
            item={item}
            index={index}
            selected={index === selectedIndex}
            onPick={onPick}
            onHover={onHover}
            testID={`${testID}-row-${rowKey(item)}`}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  box: {
    width: MENTION_SUGGESTER_WIDTH,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderPanelSoft,
    borderRadius: 6,
    overflow: 'hidden',
    shadowColor: colors.textPrimary,
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  scroll: {
    maxHeight: MAX_LIST_HEIGHT,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: ROW_HEIGHT,
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 10,
  },
  rowSelected: {
    backgroundColor: colors.surfaceMessage,
  },
  disc: {
    width: DISC,
    height: DISC,
    borderRadius: DISC / 2,
    backgroundColor: colors.surfaceMessageOther,
    alignItems: 'center',
    justifyContent: 'center',
  },
  discText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: fontSize.formLabel,
    lineHeight: 18,
    color: colors.textPrimary,
  },
  secondary: {
    fontSize: fontSize.charCount,
    lineHeight: 16,
    color: colors.textSecondary,
  },
  empty: {
    paddingHorizontal: 10,
    paddingVertical: 12,
    fontSize: 13,
    color: colors.textSecondary,
  },
});
